import React, { Dispatch, Fragment, SetStateAction, useContext, useRef, useState } from 'react';
import Drawer from '../../../common/components/Drawer/Drawer';
import { availableCarriers, calculateMovesPercentage, getCarrierById } from '../../../common/helpers/carriers';
import { formattedNumberToInt } from '../../../common/helpers/utils';
import { AdjustableComponent, useClasses } from '../../../common/hooks/useClasses';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import FormikInputField from '../../../common/components/FormikInputField/FormikInputField';
import {
  McButton,
  McDateRange,
  McInputDate,
  McModal,
  McNotification,
  McOption,
  McPopover,
  McSelect,
  McTab,
  McTabBar,
  McTable,
} from '@maersk-global/mds-react-wrapper';
import z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import FormattedMessage from '../../../common/FormattedMessage';
import { ModalContext, ModalType } from '../../../common/modals/ModalProvider';
import {
  addNewCarrier,
  editCarriersMapper,
  recalculateEntries,
  removeCarrier,
  updateEntries,
  validateEntries,
} from '../../config/mappers';
import useCapacityData from '../../hooks/useCapacityData';
import useCarrierColumnsHeader from '../../hooks/useCarrierColumnsHeader';
import ChangelogDrawer from '../changelog/changelog-drawer/ChangelogDrawer';
import { createDefaultValueDto } from '../dto/DefaultValueDto';
import { modifyDate } from '../../../common/helpers/modify-date';
import IMOInputField from '../IMODescription/IMOInputField';
import { editCarriersTableColumns } from '../soc/const';

export type EditDefaultValuesDrawerStyles = {
  field: string;
  buttons: string;
  content: string;
};

export type EditDefaultValuesDrawerProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onSuccess?: Function;
  selectedTerminal: string;
  id: string;
  defaultValues: Map<string, number>;
  type: 'yard' | 'soc';
};

const FormValidationSchemaSOC = z
  .object({
    terminalCode: z.string(),
    moves: z.number({ message: 'Value should be a number' }).positive(),
    moveCountStretchPercentage: z.number({ message: 'Value should be a number (0 - 100)' }).min(0).max(100),
    moveCountCriticalPercentage: z.number({ message: 'Value should be a number (0 - 100)' }).min(0).max(100),
    entries: z.array(
      z.object({
        moves: z.number().gte(0, 'Movecount must be greater than or equal 0'),
        capacityDataType: z.string(),
      }),
    ),
  })
  .superRefine((data, ctx) => {
    // @ts-ignore
    if (!validateEntries(data.entries, data.moves)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Residual value can not be less than 0',
        path: ['entries'],
      });
    }
  });

const FormValidationSchemaYard = z.object({
  terminalCode: z.string(),
  oogUnits: z.number({ message: 'Value should be a number' }).positive(),
  imoUnits: z.number({ message: 'Value should be a number' }).positive(),
  yardCapacity: z.number({ message: 'Value should be a number' }).positive(),
  reeferPlugs: z.number({ message: 'Value should be a number' }).positive(),
});

const EditDefaultValuesDrawer: AdjustableComponent<EditDefaultValuesDrawerProps, EditDefaultValuesDrawerStyles> = ({
  classes,
  open,
  setOpen,
  onSuccess,
  selectedTerminal,
  id,
  defaultValues,
  type,
}) => {
  const styles = useClasses(
    {
      field: 'EditDefaultValuesDrawer__field',
      content: 'EditDefaultValuesDrawer__content',
      buttons: 'EditDefaultValuesDrawer__buttons',
    },
    classes,
  );
  const formikRef = useRef(null);
  const deleteModalRef = useRef(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [msgShown, setMsgShown] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [alertShown, setAlertShown] = useState(false);
  const { updateTemplateWithCarriers, deleteTemplate } = useCapacityData();
  const { getCustomHeaders } = useCarrierColumnsHeader();
  const { showModal } = useContext(ModalContext);

  const onClose = () => {
    setOpen(false);
    setAlertShown(false);
    setActiveTab(0);
  };

  const getFormInitialValues = () => {
    const baseValues = {
      id: id,
      terminalCode: selectedTerminal,
      startDate: defaultValues['startDate'],
      previousStartDate: defaultValues['previousStartDate'],
      nextStartDate: defaultValues['nextStartDate'],
      capacityDataType: 'TOTAL',
    };
    const socValues = {
      moves: formattedNumberToInt(defaultValues['moves']),
      moveCountStretchPercentage: defaultValues['moveCountStretchPercentage'],
      moveCountCriticalPercentage: defaultValues['moveCountCriticalPercentage'],
      entries: defaultValues['entries'],
    };
    const yardValues = {
      yardCapacity: formattedNumberToInt(defaultValues['yardCapacity']),
      reeferPlugs: formattedNumberToInt(defaultValues['reeferPlugs']),
      imoUnits: formattedNumberToInt(defaultValues['imoUnits']),
      imoUnitsDescription: defaultValues['imoUnitsDescription'],
      oogUnits: formattedNumberToInt(defaultValues['oogUnits']),
      ladenCapacity: formattedNumberToInt(defaultValues['ladenCapacity']) || '',
      emptyCapacity: formattedNumberToInt(defaultValues['emptyCapacity']) || '',
      maxCapacity: formattedNumberToInt(defaultValues['maxCapacity']) || '',
      entries: defaultValues['entries'],
    };
    return { ...baseValues, ...(type === 'soc' ? socValues : yardValues) };
  };

  const onSubmit = async (values, { setSubmitting }) => {
    if (type === 'soc' && activeTab === 0) {
      setAlertShown(true);
      setActiveTab(1);
      return;
    }
    setSubmitting(true);
    const dto = createDefaultValueDto(values, type);
    try {
      // @ts-ignore
      await updateTemplateWithCarriers(dto);
      onSuccess?.();
      onClose();
      if (defaultValues['adjustedWeeks'].length) {
        showModal(ModalType.CarriersEdit, () => () => {}, { weeks: defaultValues['adjustedWeeks'] });
      }
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title={'Edit default values'}
      open={open}
      setOpen={onClose}
      onRequestClose={onClose}
      noFooter
      wider
      titleAddon={
        <div className="EditWeeklyValuesDrawer__changelog">
          <McButton
            type="button"
            appearance="secondary"
            variant="outlined"
            click={() => setChangelogOpen(true)}
            icon="arrow-anti-clockwise-clock"
          >
            <FormattedMessage id="changelog" />
          </McButton>
        </div>
      }
    >
      <Formik
        initialValues={getFormInitialValues()}
        enableReinitialize
        validationSchema={toFormikValidationSchema(
          // @ts-ignore
          type === 'soc' ? FormValidationSchemaSOC : FormValidationSchemaYard,
        )}
        onSubmit={onSubmit}
        innerRef={formikRef} // Pass the ref to Formik
      >
        {({ isSubmitting, values, setFieldValue }) => {
          const carrierData =
            type === 'soc'
              ? editCarriersMapper(
                  type,
                  // @ts-ignore
                  values.entries,
                  // @ts-ignore
                  {
                    // @ts-ignore
                    totalMoveCount: values.moves,
                    // @ts-ignore
                    stretch: values.moveCountStretchPercentage,
                    // @ts-ignore
                    critical: values.moveCountCriticalPercentage,
                  },
                )
              : [];
          return (
            <Form className="EditDefaultValuesDrawer__form">
              <div className="EditDefaultValuesDrawer__form__wrapper">
                <McTabBar tabchange={(e) => setActiveTab(e.detail)} currentindex={activeTab}>
                  <McTab slot="tab" label="Default values" />
                  <div slot="panel" style={{ paddingBottom: 0, marginBottom: -16 }}>
                    <div className={styles.content}>
                      {type === 'soc' ? (
                        <div style={{ backgroundColor: '#f0f0f0', padding: '0.5rem', borderRadius: '0.25rem' }}>
                          <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>
                            {/* Move Count */}
                            <div style={{ flexBasis: '30%', textAlign: 'left' }}>
                              <label>Move count</label>
                              <Field
                                label={''}
                                type="number"
                                name="moves"
                                component={FormikInputField}
                                input={(e) => {
                                  setFieldValue('moves', parseInt(e.target.value));
                                  setFieldValue(
                                    'entries',
                                    recalculateEntries(
                                      // @ts-ignore
                                      values.entries,
                                      // @ts-ignore
                                      getFormInitialValues().entries,
                                      // @ts-ignore
                                      getFormInitialValues().moves,
                                      e.target.value,
                                    ),
                                  );
                                }}
                              />
                              <ErrorMessage name="moves" component="div" />
                            </div>

                            {/* Threshold #1 with Icon and Total */}
                            <div style={{ flexBasis: '30%', textAlign: 'left' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ backgroundColor: '#FFD029', width: '1rem', height: '1rem' }}></span>
                                <label>Threshold L2</label>
                              </div>
                              <Field
                                label={''}
                                type="number"
                                name="moveCountStretchPercentage"
                                suffix="%"
                                component={FormikInputField}
                              />
                              <ErrorMessage name="moveCountStretchPercentage" component="div" />
                              <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: '#8A8A90' }}>
                                {/*//@ts-ignore*/}
                                Total: {printTotal(values.moves, values.moveCountStretchPercentage)}
                              </div>
                            </div>

                            {/* Threshold #2 with Icon and Total */}
                            <div style={{ flexBasis: '30%', textAlign: 'left' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ backgroundColor: '#FF6000', width: '1rem', height: '1rem' }}></span>
                                <label>Threshold L3</label>
                              </div>
                              <Field
                                label={''}
                                type="number"
                                name="moveCountCriticalPercentage"
                                suffix="%"
                                component={FormikInputField}
                              />
                              <ErrorMessage name="moveCountCriticalPercentage" component="div" />
                              <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: '#8A8A90' }}>
                                {/*// @ts-ignore*/}
                                Total: {printTotal(values.moves, values.moveCountCriticalPercentage)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', gap: '1rem' }}>
                            <Field
                              label={'Yard TEU capacity'}
                              type="number"
                              name="yardCapacity"
                              component={FormikInputField}
                            />
                            <ErrorMessage name="yardCapacity" component="div" />
                            <Field
                              label={'Reefer plugs'}
                              type="number"
                              name="reeferPlugs"
                              component={FormikInputField}
                            />
                            <ErrorMessage name="reeferPlugs" component="div" />
                          </div>
                          <br />
                          <div style={{ display: 'flex', gap: '1rem' }}>
                            <IMOInputField isDrawerOpen={open} />
                            <Field label={'OOG units'} type="number" name="oogUnits" component={FormikInputField} />
                            <ErrorMessage name="oogUnits" component="div" />
                          </div>

                          <br />
                          <div style={{ display: 'flex', gap: '1rem' }}>
                            <Field label="Laden TEUs" type="number" name="ladenCapacity" component={FormikInputField} />
                            <ErrorMessage name="ladenCapacity" component="div" />
                            <Field
                              label={'Empty TEUs'}
                              type="number"
                              name="emptyCapacity"
                              component={FormikInputField}
                            />
                            <ErrorMessage name="emptyCapacity" component="div" />
                            <Field
                              label={'Max TEU Capacity'}
                              type="number"
                              name="maxCapacity"
                              component={FormikInputField}
                            />
                            <ErrorMessage name="maxCapacity" component="div" />
                          </div>
                        </>
                      )}

                      <br />
                      <div>
                        <h4>Validity period</h4>
                        {/*// @ts-ignore*/}
                        {formikRef.current?.values?.previousStartDate && (
                          <>
                            <McNotification
                              appearance="info"
                              body={
                                'Previous period: From ' +
                                // @ts-ignore
                                formikRef.current?.values?.previousStartDate +
                                ' to ' +
                                // @ts-ignore
                                modifyDate(formikRef.current?.values?.startDate?.split('T')[0], 'remove')
                              }
                              icon="info-circle"
                              verticalalign="middle"
                              actionsposition="right"
                            />
                            <br />
                          </>
                        )}
                        {/* Validity start date */}
                        <McDateRange>
                          yearcap={20}
                          {/*set 'value'*/}
                          <McInputDate
                            slot={'to'}
                            label={'To'}
                            disabled
                            value={
                              // @ts-ignore
                              formikRef.current?.values?.nextStartDate
                                ? // @ts-ignore
                                  modifyDate(formikRef.current?.values?.nextStartDate, 'remove')
                                : '2100-01-01'
                            }
                          ></McInputDate>
                          <McInputDate
                            slot={'from'}
                            label={'From'}
                            showweeknumbers={true}
                            onMouseEnter={() => setMsgShown(Boolean(values.previousStartDate))}
                            onMouseLeave={() => setMsgShown(false)}
                            customize={[
                              {
                                date: (date) => date.getDay() === 0 || (date.getDay() <= 6 && date.getDay() >= 2),
                                disabled: true,
                              },
                            ]}
                            min={
                              // @ts-ignore
                              formikRef.current?.values?.previousStartDate &&
                              // @ts-ignore
                              modifyDate(formikRef.current?.values?.previousStartDate, 'add')
                            }
                            max={
                              // @ts-ignore
                              formikRef.current?.values?.nextStartDate &&
                              // @ts-ignore
                              modifyDate(formikRef.current?.values?.nextStartDate, 'remove')
                            }
                            // @ts-ignore
                            disabled={formikRef.current?.values?.previousStartDate === undefined}
                            // @ts-ignore
                            value={formikRef.current?.values?.startDate?.split('T')[0]}
                            input={(e) => {
                              // @ts-ignore
                              formikRef.current?.setFieldValue('startDate', e.target.value + 'T00:00:00Z');
                            }}
                          ></McInputDate>
                        </McDateRange>
                        <div style={{ position: 'relative', height: 50, marginTop: 10 }}>
                          {msgShown && (
                            <div style={{ position: 'absolute', minWidth: '100%' }}>
                              <McNotification
                                appearance="warning"
                                body="For periods shorter than 5 weeks, please adjust the individual weeks"
                                icon="exclamation-circle"
                                verticalalign="middle"
                                actionsposition="right"
                                fit="small"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {type === 'soc' && (
                    <>
                      <McTab slot="tab" label="Carriers" />
                      <div slot="panel">
                        <div className="EditDefaultValuesDrawer__add-carrier">
                          <McPopover
                            trigger="click"
                            width="auto"
                            modalmode="none"
                            position="bottom-right"
                            opendelay={50}
                          >
                            <McButton
                              slot="trigger"
                              label="Add new carrier"
                              icon="plus"
                              variant="outlined"
                              fit="small"
                            />
                            <div className="EditDefaultValuesDrawer__add-carrier-popoup">
                              <McSelect
                                label="Carrier"
                                placeholder="Select carrier"
                                name="carrier"
                                optionselected={(e) =>
                                  // @ts-ignore
                                  setFieldValue('entries', addNewCarrier(values.entries, e.target.value))
                                }
                              >
                                {availableCarriers
                                  .filter((c) => !c.required && !c.deprecated)
                                  .map((c) => (
                                    <McOption
                                      key={c.id}
                                      value={c.id}
                                      // @ts-ignore
                                      disabled={values.entries.some((x) => x.capacityDataType === c.id)}
                                    >
                                      {c.name}
                                    </McOption>
                                  ))}
                              </McSelect>
                            </div>
                          </McPopover>
                        </div>
                        {alertShown && (
                          <div className="EditDefaultValuesDrawer__carrier-warning">
                            <McNotification
                              appearance="warning"
                              body="Please confirm carriers' move count before submitting"
                              icon="exclamation-triangle"
                              verticalalign="middle"
                              actionsposition="right"
                              fit="small"
                            />
                          </div>
                        )}
                        <McTable columns={editCarriersTableColumns} data={carrierData}>
                          {getCustomHeaders().map((header) => (
                            <div key={header.id} slot={`${header.id}_header`}>
                              {header.render()}
                            </div>
                          ))}
                          {carrierData.map((row, index) => {
                            const carrier = getCarrierById(row.carrier);
                            return (
                              <Fragment key={`${row.id}_actions`}>
                                <div slot={`${row.id}_carrier`}>
                                  <div className="TerminalCarriersRenderer__logo">
                                    {carrier.image && <carrier.image />}
                                    <span className="TerminalCarriersRenderer__logo__name">{carrier.name}</span>
                                  </div>
                                </div>
                                <div slot={`${row.id}_moveCount`}>
                                  {carrier.order > -1 ? (
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                      <Field
                                        hiddenlabel
                                        name="moveCount"
                                        type="number"
                                        fit="small"
                                        component={FormikInputField}
                                        value={row.moveCount}
                                        input={(e: InputEvent) =>
                                          setFieldValue(
                                            'entries',
                                            // @ts-ignore
                                            updateEntries(values.entries, carrier.id, 'moves', e.target.value),
                                          )
                                        }
                                        min={0}
                                        width={100}
                                      />
                                      <Field
                                        hiddenlabel
                                        type="number"
                                        name="moveCountPercentage"
                                        suffix="%"
                                        component={FormikInputField}
                                        min={0}
                                        fit="small"
                                        width={80}
                                        value={calculateMovesPercentage(
                                          formattedNumberToInt(`${carrierData[0].moveCount}`),
                                          row.moveCount,
                                        )}
                                        input={(e: InputEvent) =>
                                          setFieldValue(
                                            'entries',
                                            updateEntries(
                                              // @ts-ignore
                                              values.entries,
                                              carrier.id,
                                              'percentage',
                                              // @ts-ignore
                                              e.target.value,
                                              formattedNumberToInt(`${carrierData[0].moveCount}`),
                                            ),
                                          )
                                        }
                                      />
                                    </div>
                                  ) : (
                                    row.moveCount
                                  )}
                                </div>
                                <div slot={`${row.id}_actions`}>
                                  {!carrier.required && carrier.order > -1 && (
                                    <McButton
                                      label="Delete"
                                      icon="trash"
                                      fit="small"
                                      variant="plain"
                                      appearance="primary"
                                      // @ts-ignore
                                      click={() => setFieldValue('entries', removeCarrier(values.entries, carrier.id))}
                                    />
                                  )}
                                </div>
                              </Fragment>
                            );
                          })}
                        </McTable>
                        <ErrorMessage className="EventsDrawer__error" name="entries" component="div" />
                      </div>
                    </>
                  )}
                </McTabBar>
              </div>

              <div className={styles.buttons}>
                <McButton type="button" appearance="neutral" click={() => onClose()}>
                  <FormattedMessage id="cancel" />
                </McButton>
                <McButton disabled={isSubmitting} type="submit" appearance="primary">
                  <FormattedMessage
                    id={isSubmitting ? 'updateRuleDrawerFormSubmitting' : 'updateRuleDrawerFormSubmit'}
                  />
                </McButton>
                {/*// @ts-ignore*/}
                {formikRef.current?.values?.previousStartDate && (
                  <McButton
                    type="button"
                    appearance="error"
                    click={() => {
                      if (deleteModalRef.current) {
                        // @ts-ignore
                        deleteModalRef.current.show();
                      }
                    }}
                  >
                    <FormattedMessage id="delete" />
                  </McButton>
                )}
                <McModal ref={deleteModalRef} dimension={'small'} heading={'Delete Default Value'}>
                  <p id={'question'}>Are you sure you want to delete this default value?</p>
                  <McButton
                    slot="secondaryAction"
                    appearance="neutral"
                    variant="outlined"
                    dialogaction="cancel"
                    label="Cancel"
                  />
                  <McButton
                    id="confirm"
                    slot="primaryAction"
                    appearance="primary"
                    loading={deleteLoading}
                    click={async () => {
                      setDeleteLoading(true);
                      await deleteTemplate(values?.id);
                      onClose();
                    }}
                    label="Delete"
                  />
                </McModal>
              </div>
            </Form>
          );
        }}
      </Formik>

      <ChangelogDrawer open={changelogOpen} setOpen={setChangelogOpen} id={id} service="capacity" wider />
    </Drawer>
  );
};

const printTotal = (moves: number, percentage: number) => {
  return moves && percentage ? moves + Math.ceil((moves * percentage) / 100) : '-';
};

export default EditDefaultValuesDrawer;
